import { useState } from 'react';
import { notifications } from '@mantine/notifications';
import { useUpdateOrderStatus, useRefundOrder, OrderStatus as OrderStatusEnum } from '@modules/orders';
import { Button, ErrorModal } from '@shared/ui';
import OrderReadyModal from './OrderReadyModal';
import OrderCancelModal from './OrderCancelModal';

const ERROR_CODE_DELIVERY_NOT_RETURNED = 'DELIVERY_NOT_RETURNED';

interface OrderActionsProps {
  orderId: number;
  status: string;
  deliveryMethod?: string;
}

export default function OrderActions({ orderId, status, deliveryMethod }: OrderActionsProps) {
  const updateStatusMutation = useUpdateOrderStatus();
  const refundOrderMutation = useRefundOrder();
  const [isReadyModalOpen, setIsReadyModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isReturnModalOpen, setIsReturnModalOpen] = useState(false);
  const [deliveryNotReturnedMessage, setDeliveryNotReturnedMessage] = useState<string | null>(null);

  const handleTakeInWork = async () => {
    try {
      await updateStatusMutation.mutateAsync({
        orderId,
        dto: {
          status: OrderStatusEnum.IN_PROGRESS,
          changedBy: 'ADMIN',
        },
      });

      notifications.show({
        title: 'Успешно',
        message: 'Заказ взят в работу',
        color: 'green',
      });
    } catch (error) {
      console.error('Ошибка при обновлении статуса заказа:', error);
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось обновить статус заказа',
        color: 'red',
      });
    }
  };

  const handleCancelConfirm = async (comment: string) => {
    try {
      await refundOrderMutation.mutateAsync({
        orderId,
        dto: {
          ...(comment ? { cancellationComment: comment } : {}),
        },
      });
      setIsCancelModalOpen(false);
      notifications.show({
        title: 'Успешно',
        message: 'Заказ отменён',
        color: 'green',
      });
    } catch (error: any) {
      console.error('Ошибка при отмене заказа:', error);
      const data = error?.response?.data;
      if (data?.code === ERROR_CODE_DELIVERY_NOT_RETURNED && data?.message) {
        setIsCancelModalOpen(false);
        setDeliveryNotReturnedMessage(data.message);
      } else {
        notifications.show({
          title: 'Ошибка',
          message: data?.message ?? 'Не удалось отменить заказ',
          color: 'red',
        });
      }
    }
  };

  const handleReturnConfirm = async (comment: string) => {
    try {
      await refundOrderMutation.mutateAsync({
        orderId,
        dto: {
          ...(comment ? { cancellationComment: comment } : {}),
        },
      });
      setIsReturnModalOpen(false);
      notifications.show({
        title: 'Успешно',
        message: 'Возврат оформлен',
        color: 'green',
      });
    } catch (error: any) {
      console.error('Ошибка при возврате заказа:', error);
      const data = error?.response?.data;
      if (data?.code === ERROR_CODE_DELIVERY_NOT_RETURNED && data?.message) {
        setIsReturnModalOpen(false);
        setDeliveryNotReturnedMessage(data.message);
      } else {
        notifications.show({
          title: 'Ошибка',
          message: data?.message ?? 'Не удалось оформить возврат',
          color: 'red',
        });
      }
    }
  };

  const handleReady = () => {
    setIsReadyModalOpen(true);
  };

  const handleSendToDelivery = async () => {
    try {
      await updateStatusMutation.mutateAsync({
        orderId,
        dto: {
          status: OrderStatusEnum.IN_DELIVERY,
          changedBy: 'ADMIN',
        },
      });

      notifications.show({
        title: 'Успешно',
        message: 'Заказ передан в доставку',
        color: 'green',
      });
    } catch (error) {
      console.error('Ошибка при обновлении статуса заказа:', error);
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось обновить статус заказа',
        color: 'red',
      });
    }
  };

  const handleDelivered = async () => {
    try {
      await updateStatusMutation.mutateAsync({
        orderId,
        dto: {
          status: OrderStatusEnum.DELIVERED,
          changedBy: 'ADMIN',
        },
      });

      notifications.show({
        title: 'Успешно',
        message: 'Заказ отмечен как доставлен',
        color: 'green',
      });
    } catch (error) {
      console.error('Ошибка при обновлении статуса заказа:', error);
      notifications.show({
        title: 'Ошибка',
        message: 'Не удалось обновить статус заказа',
        color: 'red',
      });
    }
  };

  // Кнопка "Возврат" - та же логика, что и отмена (refund)
  const returnButton = (
    <Button
      size="xs"
      onClick={() => setIsReturnModalOpen(true)}
      disabled={updateStatusMutation.isPending || refundOrderMutation.isPending}
      loading={refundOrderMutation.isPending}
      style={{ background: '#dc3545' }}
    >
      Возврат
    </Button>
  );

  // Кнопка "Отменить" - используется в нескольких статусах
  const cancelButton = (
    <Button
      size="xs"
      variant="secondary"
      onClick={() => setIsCancelModalOpen(true)}
      disabled={updateStatusMutation.isPending || refundOrderMutation.isPending}
      loading={refundOrderMutation.isPending}
    >
      Отменить
    </Button>
  );

  // PENDING: оплату ждём; отмена только клиентом или авто-кроном — без кнопки у админа
  if (status === OrderStatusEnum.PENDING) {
    return null;
  }

  // Показываем кнопки для статуса CONFIRMED
  if (status === OrderStatusEnum.CONFIRMED) {
    return (
      <>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Button
            size="xs"
            onClick={handleTakeInWork}
            disabled={updateStatusMutation.isPending}
            loading={updateStatusMutation.isPending}
          >
            Взять в работу
          </Button>
          {cancelButton}
        </div>
        <OrderCancelModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancelConfirm}
          isLoading={refundOrderMutation.isPending}
        />
        {deliveryNotReturnedMessage != null && (
          <ErrorModal
            isOpen
            onClose={() => setDeliveryNotReturnedMessage(null)}
            errorMessage={deliveryNotReturnedMessage}
          />
        )}
      </>
    );
  }

  // Показываем кнопки для статуса IN_PROGRESS
  if (status === OrderStatusEnum.IN_PROGRESS) {
    return (
      <>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Button
            size="xs"
            onClick={handleReady}
          >
            Готов
          </Button>
          {cancelButton}
        </div>
        <OrderReadyModal
          isOpen={isReadyModalOpen}
          onClose={() => setIsReadyModalOpen(false)}
          orderId={orderId}
        />
        <OrderCancelModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancelConfirm}
          isLoading={refundOrderMutation.isPending}
        />
        {deliveryNotReturnedMessage != null && (
          <ErrorModal
            isOpen
            onClose={() => setDeliveryNotReturnedMessage(null)}
            errorMessage={deliveryNotReturnedMessage}
          />
        )}
      </>
    );
  }

  // Показываем кнопки для статуса READY
  if (status === OrderStatusEnum.READY) {
    return (
      <>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {deliveryMethod === 'PICKUP' ? (
            <Button
              size="xs"
              onClick={handleDelivered}
              disabled={updateStatusMutation.isPending}
              loading={updateStatusMutation.isPending}
            >
              Получен
            </Button>
          ) : (
            <Button
              size="xs"
              onClick={handleSendToDelivery}
              disabled={updateStatusMutation.isPending}
              loading={updateStatusMutation.isPending}
            >
              Передать в доставку
            </Button>
          )}
          {cancelButton}
        </div>
        <OrderCancelModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancelConfirm}
          isLoading={refundOrderMutation.isPending}
        />
        {deliveryNotReturnedMessage != null && (
          <ErrorModal
            isOpen
            onClose={() => setDeliveryNotReturnedMessage(null)}
            errorMessage={deliveryNotReturnedMessage}
          />
        )}
      </>
    );
  }

  // Показываем кнопки для статуса IN_DELIVERY
  if (status === OrderStatusEnum.IN_DELIVERY) {
    return (
      <>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <Button
            size="xs"
            onClick={handleDelivered}
            disabled={updateStatusMutation.isPending}
            loading={updateStatusMutation.isPending}
          >
            Доставлен
          </Button>
          {cancelButton}
        </div>
        <OrderCancelModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          onConfirm={handleCancelConfirm}
          isLoading={refundOrderMutation.isPending}
        />
        {deliveryNotReturnedMessage != null && (
          <ErrorModal
            isOpen
            onClose={() => setDeliveryNotReturnedMessage(null)}
            errorMessage={deliveryNotReturnedMessage}
          />
        )}
      </>
    );
  }

  // Показываем кнопку возврата для статуса DELIVERED (Доставлен)
  if (status === OrderStatusEnum.DELIVERED) {
    return (
      <>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {returnButton}
        </div>
        <OrderCancelModal
          isOpen={isReturnModalOpen}
          onClose={() => setIsReturnModalOpen(false)}
          onConfirm={handleReturnConfirm}
          isLoading={refundOrderMutation.isPending}
          title="Возврат заказа?"
          confirmButtonText="Оформить возврат"
          placeholder="Причина возврата (необязательно)"
          reasonRequired={false}
          description="Оформить возврат по доставленному заказу. Можно указать причину — она сохранится в истории."
        />
        {deliveryNotReturnedMessage != null && (
          <ErrorModal
            isOpen
            onClose={() => setDeliveryNotReturnedMessage(null)}
            errorMessage={deliveryNotReturnedMessage}
          />
        )}
      </>
    );
  }

  return null;
}
